<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useConfirm } from 'primevue/useconfirm';
import { useAToast } from '@Atoms';
import { useAnimalProfileStore, useSessionStore } from '@Stores';
import { RESPONSE_MESSAGES, RESPONSE_STATUS } from './registerResponses.constants';
import { registerMicrochipService } from '../../../services';

const props = defineProps<{
  field: any;
  model: any;
}>();

const { shelterProfile } = storeToRefs(useSessionStore());
const { animal } = storeToRefs(useAnimalProfileStore());

const { t } = useI18n();
const { notify, TOAST_TYPES } = useAToast();
const confirm = useConfirm();

const sendingMicrochip = ref(false);
const registrationSuccess = ref(false);
const registerResponse = ref();

const microchip = computed(() => animal.value?.microchip);
const microchip_issuer = computed(() => props.model);
const showMicrochipButton = computed(
  () =>
    microchipLabel.value !== null &&
    animal.value.microchip_registration !== 'success' &&
    registerResponse.value?.status !== 'ok'
);
const registerResponseError = computed(() =>
  [
    RESPONSE_MESSAGES.MICROCHIP_EXISTS,
    RESPONSE_MESSAGES.TRANSPONDER_EXISTS,
    RESPONSE_MESSAGES.FIELDS_ERROR,
  ].includes(registerResponse.value?.errorMessage || animal.value?.microchip_error_message)
);
const microchipLabel = computed(() => {
  if (
    shelterProfile.value.chip_integration &&
    microchip.value &&
    microchip.value !== '' &&
    animal.value.outcome_id &&
    animal.value.owner_id &&
    animal.value.microchip_registration !== 'success'
  ) {
    if (shelterProfile.value.petlink_enabled && microchip_issuer.value === 'PetLink') {
      return 'components.forms.microchipRegister.labelPetLink';
    }

    if (shelterProfile.value.homeagain_enabled && microchip_issuer.value === 'HomeAgain') {
      return 'components.forms.microchipRegister.labelHomeAgain';
    }

    if (
      !registrationSuccess.value &&
      shelterProfile.value.buddy_id_enabled &&
      microchip_issuer.value === 'BuddyID'
    ) {
      return 'components.forms.microchipRegister.labelBuddyId';
    }

    if (
      !registrationSuccess.value &&
      shelterProfile.value.akc_reunite_enabled &&
      microchip_issuer.value === 'AKC Reunite'
    ) {
      return 'components.forms.microchipRegister.labelAkcReunite';
    }
  }

  return null;
});

function onConfirmRegister(event: any) {
  confirm.require({
    group: 'registerMicrochip',
    target: event.currentTarget,
    acceptIcon: 'pi pi-check-circle',
    rejectClass: 'p-button-secondary p-button-sm text-xs',
    acceptClass: 'p-button-sm text-xs af-button-success',
    rejectLabel: t('common.cancel'),
    acceptLabel: t('components.forms.microchipRegister.register'),
    accept: () => registerMicrochip(),
  });
}

function registerMicrochip() {
  sendingMicrochip.value = true;

  const PAYLOAD = {
    animal: animal.value,
    microchip: microchip.value,
    microchip_issuer: microchip_issuer.value,
  };

  registerMicrochipService(PAYLOAD)
    .then((response) => {
      registerResponse.value = response;
      registrationSuccess.value = true;

      const TOAST_ERROR = {
        title: 'components.forms.toast.title.microchipRegisterError',
        message: registerResponse.value?.errorMessage,
        type: TOAST_TYPES.ERROR,
      };
      const TOAST_SUCCESS = {
        title: 'components.forms.toast.title.microchipRegister',
        type: TOAST_TYPES.SUCCESS,
      };

      if (response.status == 'error') return notify(TOAST_ERROR);
      if (response.status == 'ok') return notify(TOAST_SUCCESS);
    })
    .catch((error) => (registerResponse.value = error))
    .finally(() => (sendingMicrochip.value = false));
}
</script>

<template>
  <Button
    v-if="showMicrochipButton"
    outlined
    class="af-button w-fit border-none !p-1 !text-xs"
    severity="info"
    icon="pi pi-cloud-upload"
    :label="$t(microchipLabel || '')"
    :loading="sendingMicrochip"
    @click="onConfirmRegister"
  />

  <Message
    v-if="
      (registerResponse && registerResponse.status === RESPONSE_STATUS.ERROR) ||
      (!registerResponse && animal.microchip_registration === RESPONSE_STATUS.ERROR)
    "
    class="my-0"
    severity="error"
    :closable="false"
  >
    <p class="text-xs">
      {{ registerResponse?.errorMessage || animal.microchip_error_message }}
    </p>

    <p
      v-if="registerResponseError"
      class="text-xs"
    >
      {{ $t('components.forms.microchipRegister.contactProvider') }}
    </p>
  </Message>

  <Message
    v-else-if="
      (registerResponse && registerResponse.status === RESPONSE_STATUS.OK) ||
      animal.microchip_registration === RESPONSE_STATUS.SUCCESS
    "
    class="my-0"
    severity="success"
    pt:icon="!text-xs"
    pt:closeButton="p-0 w-fit h-fit"
    pt:closeIcon="!text-xs"
    :closable="false"
  >
    <p class="text-xs">{{ $t('components.forms.microchipRegister.microchipRegistered') }}</p>
  </Message>

  <aside>
    <ConfirmPopup group="registerMicrochip">
      <template #message>
        <div class="flex flex-col items-center justify-center gap-3 p-4 text-center">
          <i class="pi pi-cloud-upload text-3xl text-sky-500"></i>

          <div>
            <h2 class="font-medium">
              {{ t('components.forms.microchipRegister.registerHeader') }}
            </h2>

            <p class="text-sm">{{ t('components.forms.microchipRegister.registerMessage') }}</p>
          </div>
        </div>
      </template>
    </ConfirmPopup>
  </aside>
</template>
